import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { authApi, whatsappApi } from '@/services/api';
import SetupGuidePanel from '@/components/settings/SetupGuidePanel';
import { WHATSAPP_SHOP_SETUP_GUIDE } from '@/components/settings/setupGuides';
import {
  MessageCircle, Save, Loader2, CheckCircle, AlertTriangle, RefreshCw,
  Eye, EyeOff, Copy, Link2, Radio,
} from 'lucide-react';

export default function WhatsAppShopTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [showToken, setShowToken] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [syncResult, setSyncResult] = useState<{ success: boolean; message: string } | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [copied, setCopied] = useState('');

  const { data: profile, isLoading } = useQuery({
    queryKey: ['profile'],
    queryFn: async () => {
      const res = await authApi.getCompanyProfile();
      return res.data.message;
    },
  });

  const { data: status } = useQuery({
    queryKey: ['whatsappCatalogStatus'],
    queryFn: async () => {
      const res = await whatsappApi.getCatalogStatus();
      return res.data.message as {
        webhook_url: string;
        marked_products: number;
        catalog_id?: string;
        whatsapp_verify_token?: string;
      };
    },
  });

  const [form, setForm] = useState({
    enable_whatsapp_shop: false,
    whatsapp_phone_number_id: '',
    whatsapp_waba_id: '',
    whatsapp_catalog_id: '',
    whatsapp_verify_token: '',
    whatsapp_business_display_name: '',
    whatsapp_access_token: '',
    whatsapp_app_secret: '',
  });

  useEffect(() => {
    if (profile) {
      setForm({
        enable_whatsapp_shop: profile.enable_whatsapp_shop === 1 || profile.enable_whatsapp_shop === true,
        whatsapp_phone_number_id: profile.whatsapp_phone_number_id || '',
        whatsapp_waba_id: profile.whatsapp_waba_id || '',
        whatsapp_catalog_id: profile.whatsapp_catalog_id || '',
        whatsapp_verify_token: profile.whatsapp_verify_token || '',
        whatsapp_business_display_name: profile.whatsapp_business_display_name || '',
        whatsapp_access_token: '',
        whatsapp_app_secret: '',
      });
    }
  }, [profile]);

  const saveMutation = useMutation({
    mutationFn: (data: typeof form) => authApi.updateCompanyProfile(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['profile'] });
      qc.invalidateQueries({ queryKey: ['whatsappCatalogStatus'] });
      setSaveSuccess(true);
      setSaveError('');
      setTimeout(() => setSaveSuccess(false), 4000);
    },
    onError: (err: any) => {
      setSaveError(err.response?.data?.message || err.message || t('whatsapp.saveFailed'));
    },
  });

  const testMutation = useMutation({
    mutationFn: () => whatsappApi.testConnection(),
    onSuccess: (res) => {
      const msg = res.data.message;
      setTestResult({ success: msg?.success ?? true, message: msg?.message || t('whatsapp.connectionOk') });
    },
    onError: (err: any) => {
      setTestResult({ success: false, message: err.response?.data?.message || err.message || t('whatsapp.connectionFailed') });
    },
  });

  const syncMutation = useMutation({
    mutationFn: () => whatsappApi.syncCatalog(),
    onSuccess: (res) => {
      const msg = res.data.message;
      setSyncResult({
        success: true,
        message: t('whatsapp.syncSuccess', { count: msg?.synced ?? 0 }),
      });
      qc.invalidateQueries({ queryKey: ['whatsappCatalogStatus'] });
    },
    onError: (err: any) => {
      setSyncResult({ success: false, message: err.response?.data?.message || err.message || t('whatsapp.syncFailed') });
    },
  });

  const regenMutation = useMutation({
    mutationFn: () => whatsappApi.regenerateVerifyToken(),
    onSuccess: (res) => {
      const token = res.data.message?.whatsapp_verify_token || '';
      setForm((f) => ({ ...f, whatsapp_verify_token: token }));
      qc.invalidateQueries({ queryKey: ['profile'] });
      qc.invalidateQueries({ queryKey: ['whatsappCatalogStatus'] });
    },
  });

  const copy = (label: string, value: string) => {
    void navigator.clipboard.writeText(value);
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  const webhookUrl = status?.webhook_url || '';

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
          <MessageCircle className="w-5 h-5" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-gray-900">{t('whatsapp.title')}</h2>
          <p className="text-sm text-gray-500">{t('whatsapp.subtitle')}</p>
        </div>
      </div>

      <SetupGuidePanel guide={WHATSAPP_SHOP_SETUP_GUIDE} accent="green" />

      {saveSuccess && (
        <div className="p-3 bg-emerald-50 text-emerald-800 rounded-lg text-sm flex items-center gap-2">
          <CheckCircle className="w-4 h-4" /> {t('whatsapp.saved')}
        </div>
      )}
      {saveError && (
        <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> {saveError}
        </div>
      )}

      <label className="flex items-center gap-3 cursor-pointer">
        <input
          type="checkbox"
          className="w-5 h-5 rounded accent-emerald-600"
          checked={form.enable_whatsapp_shop}
          onChange={(e) => setForm({ ...form, enable_whatsapp_shop: e.target.checked })}
        />
        <span className="text-sm font-medium text-gray-900">{t('whatsapp.enable')}</span>
      </label>

      {form.enable_whatsapp_shop && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('whatsapp.phoneNumberId')}</label>
              <input className="input-field w-full" value={form.whatsapp_phone_number_id} onChange={(e) => setForm({ ...form, whatsapp_phone_number_id: e.target.value })} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('whatsapp.wabaId')}</label>
              <input className="input-field w-full" value={form.whatsapp_waba_id} onChange={(e) => setForm({ ...form, whatsapp_waba_id: e.target.value })} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('whatsapp.catalogId')}</label>
              <input className="input-field w-full" value={form.whatsapp_catalog_id} onChange={(e) => setForm({ ...form, whatsapp_catalog_id: e.target.value })} placeholder={t('whatsapp.catalogIdHint')} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('whatsapp.displayName')}</label>
              <input className="input-field w-full" value={form.whatsapp_business_display_name} onChange={(e) => setForm({ ...form, whatsapp_business_display_name: e.target.value })} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('whatsapp.accessToken')}</label>
              <div className="relative">
                <input
                  type={showToken ? 'text' : 'password'}
                  className="input-field w-full pr-10"
                  value={form.whatsapp_access_token}
                  onChange={(e) => setForm({ ...form, whatsapp_access_token: e.target.value })}
                  placeholder={profile?.has_whatsapp_token ? '••••••••' : ''}
                />
                <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400" onClick={() => setShowToken((v) => !v)}>
                  {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">{t('whatsapp.tokenHint')}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('whatsapp.appSecret')}</label>
              <div className="relative">
                <input
                  type={showSecret ? 'text' : 'password'}
                  className="input-field w-full pr-10"
                  value={form.whatsapp_app_secret}
                  onChange={(e) => setForm({ ...form, whatsapp_app_secret: e.target.value })}
                  placeholder={profile?.has_whatsapp_app_secret ? '••••••••' : ''}
                />
                <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400" onClick={() => setShowSecret((v) => !v)}>
                  {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('whatsapp.verifyToken')}</label>
              <div className="flex gap-2">
                <input className="input-field flex-1" value={form.whatsapp_verify_token} onChange={(e) => setForm({ ...form, whatsapp_verify_token: e.target.value })} />
                <button type="button" onClick={() => regenMutation.mutate()} className="btn-secondary flex items-center gap-1.5 whitespace-nowrap">
                  {regenMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  {t('whatsapp.regenerate')}
                </button>
                <button type="button" onClick={() => copy('verify', form.whatsapp_verify_token)} className="btn-secondary px-3">
                  <Copy className="w-4 h-4" />
                </button>
              </div>
              {copied === 'verify' && <p className="text-xs text-emerald-600 mt-1">{t('whatsapp.copied')}</p>}
            </div>
          </div>

          <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-800">
              <Link2 className="w-4 h-4" /> {t('whatsapp.webhookUrl')}
            </div>
            <div className="flex gap-2">
              <code className="flex-1 text-xs bg-white border border-gray-200 rounded px-3 py-2 break-all">{webhookUrl}</code>
              <button type="button" onClick={() => copy('url', webhookUrl)} className="btn-secondary px-3">
                <Copy className="w-4 h-4" />
              </button>
            </div>
            {copied === 'url' && <p className="text-xs text-emerald-600">{t('whatsapp.copied')}</p>}
            <p className="text-xs text-gray-500">{t('whatsapp.webhookHelp')}</p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => testMutation.mutate()}
              disabled={testMutation.isPending}
              className="btn-secondary flex items-center gap-2"
            >
              {testMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Radio className="w-4 h-4" />}
              {t('whatsapp.testConnection')}
            </button>
            <button
              type="button"
              onClick={() => syncMutation.mutate()}
              disabled={syncMutation.isPending}
              className="btn-secondary flex items-center gap-2"
            >
              {syncMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              {t('whatsapp.syncCatalog')} ({status?.marked_products ?? 0})
            </button>
          </div>

          {testResult && (
            <div className={`p-3 rounded-lg text-sm ${testResult.success ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-700'}`}>
              {testResult.message}
            </div>
          )}
          {syncResult && (
            <div className={`p-3 rounded-lg text-sm ${syncResult.success ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-700'}`}>
              {syncResult.message}
            </div>
          )}
        </>
      )}

      <div className="pt-2">
        <button
          type="button"
          onClick={() => saveMutation.mutate(form)}
          disabled={saveMutation.isPending}
          className="btn-primary flex items-center gap-2"
        >
          {saveMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {t('common.save')}
        </button>
      </div>
    </div>
  );
}
